import { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { CreditCard, Plus, Trash, Check, ShieldCheck } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';

export default function PaymentPage() {
  const { t } = useLanguage();
  const [cards, setCards] = useState([]);
  const [showAddCard, setShowAddCard] = useState(false);
  const [form, setForm] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardHolder: ''
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    let { name, value } = e.target;
    
    // Format card number with spaces
    if (name === 'cardNumber') {
      value = value.replace(/\s/g, '').replace(/(\d{4})/g, '$1 ').trim();
      if (value.length > 19) value = value.slice(0, 19);
    }
    
    // Format expiry date
    if (name === 'expiryDate') {
      value = value.replace(/\D/g, '');
      if (value.length > 2) {
        value = value.slice(0, 2) + '/' + value.slice(2, 4);
      }
    }
    
    // Limit CVV
    if (name === 'cvv') {
      value = value.replace(/\D/g, '').slice(0, 4);
    }
    
    setForm({ ...form, [name]: value });
  };

  const handleAddCard = (e) => {
    e.preventDefault();
    
    if (form.cardNumber.replace(/\s/g, '').length < 16) {
      toast.error('Invalid card number');
      return;
    }
    
    setSaving(true);
    
    // Mock adding card
    setTimeout(() => {
      const newCard = {
        id: `card_${Date.now()}`,
        last4: form.cardNumber.replace(/\s/g, '').slice(-4),
        brand: form.cardNumber.startsWith('4') ? 'Visa' : 'Mastercard',
        expiryDate: form.expiryDate,
        cardHolder: form.cardHolder,
        isDefault: cards.length === 0
      };
      
      setCards([...cards, newCard]);
      setShowAddCard(false);
      setForm({ cardNumber: '', expiryDate: '', cvv: '', cardHolder: '' });
      setSaving(false);
      toast.success(t('success'));
    }, 1000);
  };

  const handleSetDefault = (cardId) => {
    setCards(cards.map(card => ({
      ...card,
      isDefault: card.id === cardId
    })));
    toast.success(t('success'));
  };

  const handleDeleteCard = (cardId) => {
    setCards(cards.filter(card => card.id !== cardId));
    toast.success('Card removed');
  };

  return (
    <div className="min-h-screen bg-[#050505] safe-bottom">
      {/* Header */}
      <div className="p-4 pt-6">
        <h1 className="text-2xl font-semibold tracking-tight font-['Outfit']">{t('paymentMethods')}</h1>
        <p className="text-white/50 mt-1">Manage your payment cards</p>
      </div>

      {/* Security Notice */}
      <motion.div
        className="mx-4 mb-4"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-center gap-3 p-4 rounded-2xl bg-[#0ABAB5]/10 border border-[#0ABAB5]/30">
          <ShieldCheck size={24} weight="duotone" className="text-[#0ABAB5] flex-shrink-0" />
          <p className="text-sm text-white/70">
            Your payment information is encrypted and secure. We never store your full card details.
          </p>
        </div>
      </motion.div>

      {/* Cards List */}
      <div className="px-4 space-y-3">
        {cards.length === 0 ? (
          <motion.div
            className="flex flex-col items-center justify-center py-12"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center mb-4">
              <CreditCard size={40} weight="duotone" className="text-white/20" />
            </div>
            <p className="text-white/50 mb-4">{t('noCards')}</p>
            <Button
              onClick={() => setShowAddCard(true)}
              className="h-12 px-6 bg-[#0ABAB5] hover:bg-[#0ABAB5]/90 text-black font-semibold rounded-2xl"
              data-testid="add-first-card-btn"
            >
              <Plus size={20} weight="bold" className="mr-2" />
              {t('addCard')}
            </Button>
          </motion.div>
        ) : (
          <>
            {cards.map((card, index) => (
              <motion.div
                key={card.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`p-4 rounded-2xl border transition-all ${
                  card.isDefault 
                    ? 'bg-[#0ABAB5]/10 border-[#0ABAB5]/30' 
                    : 'bg-[#121212] border-white/5'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      card.brand === 'Visa' ? 'bg-blue-500/20' : 'bg-orange-500/20'
                    }`}>
                      <CreditCard size={24} weight="duotone" className={
                        card.brand === 'Visa' ? 'text-blue-400' : 'text-orange-400'
                      } />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-mono font-semibold">•••• {card.last4}</p>
                        {card.isDefault && (
                          <span className="px-2 py-0.5 text-xs bg-[#0ABAB5]/20 text-[#0ABAB5] rounded-full font-medium">
                            {t('defaultCard')}
                          </span>
                        )}
                      </div>
                      <p className="text-white/50 text-sm">{card.brand} • {card.expiryDate}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!card.isDefault && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleSetDefault(card.id)}
                        className="text-white/50 hover:text-[#0ABAB5] hover:bg-[#0ABAB5]/10"
                      >
                        <Check size={20} />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteCard(card.id)}
                      className="text-white/50 hover:text-red-400 hover:bg-red-500/10"
                    >
                      <Trash size={20} />
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}

            <Button
              onClick={() => setShowAddCard(true)}
              variant="outline"
              className="w-full h-14 border-dashed border-white/20 bg-transparent hover:bg-white/5 rounded-2xl mt-4"
              data-testid="add-another-card-btn"
            >
              <Plus size={20} className="mr-2" />
              {t('addCard')}
            </Button>
          </>
        )}
      </div>

      {/* Demo Notice */}
      <div className="mx-4 mt-6">
        <p className="text-xs text-center text-white/30">
          {t('paymentMocked')} - No real charges will be made
        </p>
      </div>

      {/* Add Card Dialog */}
      <Dialog open={showAddCard} onOpenChange={setShowAddCard}>
        <DialogContent className="bg-[#121212] border-white/10 max-w-md mx-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-['Outfit']">{t('addCard')}</DialogTitle>
            <DialogDescription className="text-white/50">
              Enter your card details below
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleAddCard} className="space-y-4 mt-4">
            <div>
              <label className="text-sm text-white/60 mb-2 block">{t('cardNumber')}</label>
              <Input
                name="cardNumber"
                placeholder="1234 5678 9012 3456"
                value={form.cardNumber}
                onChange={handleChange}
                className="h-14 bg-[#0a0a0a] border-white/10 rounded-xl text-white font-mono placeholder:text-white/30 focus:border-[#0ABAB5]"
                data-testid="card-number-input"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-white/60 mb-2 block">{t('expiryDate')}</label>
                <Input
                  name="expiryDate"
                  placeholder="MM/YY"
                  value={form.expiryDate}
                  onChange={handleChange}
                  className="h-12 bg-[#0a0a0a] border-white/10 rounded-xl text-white font-mono placeholder:text-white/30 focus:border-[#0ABAB5]"
                  data-testid="expiry-input"
                  required
                />
              </div>
              <div>
                <label className="text-sm text-white/60 mb-2 block">{t('cvv')}</label>
                <Input
                  name="cvv"
                  type="password"
                  placeholder="•••"
                  value={form.cvv}
                  onChange={handleChange}
                  className="h-12 bg-[#0a0a0a] border-white/10 rounded-xl text-white font-mono placeholder:text-white/30 focus:border-[#0ABAB5]"
                  data-testid="cvv-input"
                  required
                />
              </div>
            </div>

            <div>
              <label className="text-sm text-white/60 mb-2 block">{t('cardHolder')}</label>
              <Input
                name="cardHolder"
                placeholder="John Doe"
                value={form.cardHolder}
                onChange={handleChange}
                className="h-12 bg-[#0a0a0a] border-white/10 rounded-xl text-white uppercase placeholder:text-white/30 placeholder:normal-case focus:border-[#0ABAB5]"
                data-testid="cardholder-input"
                required
              />
            </div>

            <Button
              type="submit"
              disabled={saving}
              className="w-full h-14 bg-[#0ABAB5] hover:bg-[#0ABAB5]/90 text-black font-semibold rounded-2xl glow-primary mt-2"
              data-testid="save-card-btn"
            >
              {saving ? t('loading') : t('addCard')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
